import { DocumentType } from '../enums.js';

export enum RouteStatus {
  Unknown = 'Unknown',
  ToDo = 'To do',
  Done = 'Done',
  Ready = 'Ready', // TODO : Consider separate SectionStatus Later
  Locked = 'Locked',
  Requested = 'Requested',
}

export const ROUTE_STATUS_TO_INDEX: RouteStatus[] = [
  RouteStatus.Unknown, // 0
  RouteStatus.ToDo, // 1
  RouteStatus.Done, // 2
  RouteStatus.Ready, // 3
  RouteStatus.Locked, // 4
  RouteStatus.Requested, // 5
];

export interface RouteConfig {
  sections: RouteSection[];
  defaultTask: DefaultTask;
  givenName?: string;
  email?: string;
  verifiedApplicant?: boolean;
  applicantEmail?: string;
}

export interface RouteSection {
  label: SectionLabel;
  route: string;
  status?: RouteStatus;
  taskGroups: RouteTaskGroup[];
}

export interface RouteTaskGroup {
  label: YourInformationTaskGroupLabel | ApplicationTaskGroupLabel | OfferTaskGroupLabel;
  status?: RouteStatus;
  tasks: RouteTask[];
}

// We can't know what this will be at design time
// because it is sourced from a Front End free text field
// The weird syntax is to placate SonarCube
type CustomRequirementTaskLabel = `${string}`;

export interface RouteTask {
  label: YourInformationTaskLabel | ApplicationTaskLabel | OfferTaskLabel | CustomRequirementTaskLabel;
  route: string;
  status?: RouteStatus;
}
export interface DefaultTask {
  label: SectionLabel.Home | YourInformationTaskLabel | ApplicationTaskLabel | OfferTaskLabel;
  route: string;
}

export enum SectionLabel {
  Home = 'Home',
  YourInformation = 'Your information',
  Application = 'Application',
  Programmes = 'Programmes',
}

export enum YourInformationTaskGroupLabel {
  AboutYou = 'About you',
  YourQualifications = 'Your qualifications',
}

export enum ApplicationTaskGroupLabel {
  PersonalInformation = 'Personal information',
  QualificationsExperience = 'Qualifications & experience',
  SupportingInformation = 'Supporting information',
  AboutYourApplication = 'About your application',
  Consent = 'Consent',
}

export enum OfferTaskGroupLabel {
  Acceptance = 'Acceptance',
  Conditions = 'Conditions',
  Finance = 'Payment',
  Completed = 'Completed',
  Tasks = 'Tasks',
}

export enum YourInformationTaskLabel {
  ContactDetails = 'Contact details',
  FeeStatus = 'Fee status',
  ProgrammeChoices = 'Programme Choices',
  HighestStudy = 'Your highest study',
}

export enum ApplicationTaskLabel {
  Identity = 'Identity',
  NationalityResidence = 'Nationality & residence',
  ContactDetails = 'Contact details',
  EducationHistory = 'Education history',
  EnglishLanguage = 'English language',
  Employment = 'Employment history',
  References = 'References',
  PersonalStatement = 'Personal statement',
  ResearchProposal = 'Research proposal',
  Funding = 'Funding',
  ImmigrationHistory = 'Immigration history',
  DiversityInclusion = 'Diversity & inclusion',
  Representation = 'Representation',
  Referral = 'Referral',
  Declarations = 'Declarations',
  InformationProcessing = 'Information processing',
  PrivacyPolicy = 'Privacy policy',
  TermsConditions = 'Terms & conditions',
  AccuracyOfData = 'Accuracy of data',
}

export enum OfferTaskLabel {
  ConditionalOffer = 'Conditional offer',
  UnconditionalOffer = 'Unconditional offer',
  Passport = 'Passport',
  PhotoID = 'Photo ID',
  AcademicResults = 'Academic requirement', //TODO: update this requirementId with sensible name on migration script writing
  FinalAcademicResults = 'Final academic results',
  AcademicCertificate = 'Academic certificate',
  AcademicTranscript = 'Academic transcript',
  EnglishLanguageResults = 'English language results',
  EnglishLanguageCertificate = 'Proof of English proficiency',
  ProofOfManagerialExperience = 'Proof of 2 years of management experience',
  ProofOfAddress = 'Proof of address',
  ProofOfNameChange = 'Proof of name change',
  NameAffidavit = 'Name Affidavit',
  BirthCert = 'Birth certificate',
  CV = 'CV to support application',
  ProofOfImmigrationStatus = 'Proof of immigration status',
  LetterOfRecommendation = 'Letter of recommendation',
  PersonalStatement = 'Personal statement',
  Portfolio = 'Portfolio',
  Payment = 'Payment',
  ProofOfPayment = 'Proof of payment',
  ResearchProposal = 'Research proposal',
  CalendlyInterview = 'Schedule interview',
  RecordedInterview = 'Record your interview',
  RefereeDetails = 'Referee details',
  Custom = 'Custom requirement',
  StaffOnly = 'Staff only requirement',
}

export enum SectionRoute {
  Home = '/',
  YourInformation = '/your-information',
  Application = '/applications/{applicationId}',
  Programmes = '/programmes/{offerId}',
}

/*
These enums are not currently used; keeping for reference in case this causes a regression
export enum AgentSectionRoute {
  Home = '/application/{uni}/{applicantId}',
  YourInformation = '/application/{uni}/{applicantId}/your-information',
  Application = '/application/{uni}/{applicantId}/applications/{applicationId}',
  Offers = '/application/{uni}/{applicantId}/offers/{offerId}'
}

export enum StaffSectionRoute {
  Home = 'staff/application/{uni}/{applicantId}',
  YourInformation = 'staff/application/{uni}/{applicantId}/your-information',
  Application = 'staff/application/{uni}/{applicantId}/applications/{applicationId}',
  Offers = 'staff/application/{uni}/{applicantId}/offers/{offerId}'
}
*/

export enum YourInformationTaskRoute {
  ContactDetails = '/your-information/contact-details',
  FeeStatus = '/your-information/fee-status',
  ProgrammeChoices = '/your-information/programme-choices',
  HighestStudy = '/your-information/your-highest-study',
}

export enum ApplicationTaskRoute {
  Identity = '/applications/{applicationId}/identity',
  NationalityResidence = '/applications/{applicationId}/nationality-and-residence',
  ContactDetails = '/applications/{applicationId}/contact-details',
  EducationHistory = '/applications/{applicationId}/education-history',
  EnglishLanguage = '/applications/{applicationId}/english-language',
  Employment = '/applications/{applicationId}/employment',
  References = '/applications/{applicationId}/references',
  PersonalStatement = '/applications/{applicationId}/personal-statement',
  ResearchProposal = '/applications/{applicationId}/research-proposal',
  Funding = '/applications/{applicationId}/funding',
  ImmigrationHistory = '/applications/{applicationId}/immigration-history',
  DiversityInclusion = '/applications/{applicationId}/diversity-inclusion',
  Representation = '/applications/{applicationId}/representation',
  Referral = '/applications/{applicationId}/referral',
  Declarations = '/applications/{applicationId}/declarations',
  InformationProcessing = '/applications/{applicationId}/information-processing',
  PrivacyPolicy = '/applications/{applicationId}/privacy-policy',
  TermsConditions = '/applications/{applicationId}/terms-conditions',
  AccuracyOfData = '/applications/{applicationId}/accuracy-of-data',
}

export enum OfferTaskRoute {
  ConditionalOffer = '/programmes/{offerId}/offer-acceptance',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  UnconditionalOffer = '/programmes/{offerId}/offer-acceptance',

  Passport = '/programmes/{offerId}/passport',
  PhotoID = '/programmes/{offerId}/photo-id',
  AcademicResults = '/programmes/{offerId}/academic-results',
  FinalAcademicResults = '/programmes/{offerId}/final-academic-results',
  AcademicCertificate = '/programmes/{offerId}/academic-certificate',
  AcademicTranscript = '/programmes/{offerId}/academic-transcript',
  EnglishLanguageResults = '/programmes/{offerId}/english-language-results',
  EnglishLanguageCertificate = '/programmes/{offerId}/english-language-certificate',
  ProofOfManagerialExperience = '/programmes/{offerId}/proof-of-managerial-experience',
  ProofOfAddress = '/programmes/{offerId}/proof-of-address',
  ProofOfNameChange = '/programmes/{offerId}/proof-of-name-change',
  NameAffidavit = '/programmes/{offerId}/name-affidavit',
  BirthCert = '/programmes/{offerId}/birth-cert',
  CV = '/programmes/{offerId}/cv',
  ProofOfImmigrationStatus = '/programmes/{offerId}/proof-of-immigration-status',
  LetterOfRecommendation = '/programmes/{offerId}/letter-of-recommendation',
  PersonalStatement = '/programmes/{offerId}/personal-statement',
  Payment = '/programmes/{offerId}/payment',
  ProofOfPayment = '/programmes/{offerId}/proof-of-payment',
  RequirementPayment = '/programmes/{offerId}/requirement-payment',
  RequirementProofOfPayment = '/programmes/{offerId}/requirement-proof-of-payment',
  Portfolio = '/programmes/{offerId}/portfolio',
  ResearchProposal = '/programmes/{offerId}/research-proposal',
  RefereeDetails = '/programmes/{offerId}/referee-details',
  CalendlyInterview = '/programmes/{offerId}/calendly-interview/{eventType}',
  RecordedInterview = '/programmes/{offerId}/recorded-interview/{interviewId}',
  Custom = `/programmes/{offerId}/{requirementId}`,
}

export type DocumentsPerRoute = Partial<
  Record<YourInformationTaskRoute | ApplicationTaskRoute | OfferTaskRoute, DocumentType[]>
>;

export const documentsPerRoute: DocumentsPerRoute = {
  [YourInformationTaskRoute.HighestStudy]: [DocumentType.AcademicTranscript],
  [ApplicationTaskRoute.Identity]: [DocumentType.Passport, DocumentType.PhotoId],
  [ApplicationTaskRoute.EducationHistory]: [DocumentType.AcademicTranscript],
  [ApplicationTaskRoute.PersonalStatement]: [DocumentType.PersonalStatement],
  [ApplicationTaskRoute.Employment]: [DocumentType.CV],
  [ApplicationTaskRoute.References]: [DocumentType.ReferenceLetter],
  [ApplicationTaskRoute.ResearchProposal]: [DocumentType.ResearchProposal],
  [ApplicationTaskRoute.EnglishLanguage]: [DocumentType.EnglishTest],
  [OfferTaskRoute.AcademicTranscript]: [DocumentType.AcademicTranscript],
  [OfferTaskRoute.AcademicCertificate]: [DocumentType.AcademicCertificate],

  [OfferTaskRoute.CV]: [DocumentType.CV],
  [OfferTaskRoute.EnglishLanguageCertificate]: [DocumentType.EnglishTest],
  [OfferTaskRoute.LetterOfRecommendation]: [DocumentType.ReferenceLetter],
  [OfferTaskRoute.Passport]: [DocumentType.Passport],
  [OfferTaskRoute.PersonalStatement]: [DocumentType.PersonalStatement],
  [OfferTaskRoute.PhotoID]: [DocumentType.PhotoId],
  [OfferTaskRoute.Portfolio]: [DocumentType.Portfolio],
  [OfferTaskRoute.ProofOfAddress]: [DocumentType.ProofOfAddress],
  [OfferTaskRoute.ProofOfNameChange]: [DocumentType.ProofOfNameChange],
  [OfferTaskRoute.NameAffidavit]: [DocumentType.NameAffidavit],
  [OfferTaskRoute.BirthCert]: [DocumentType.BirthCert],
  [OfferTaskRoute.ProofOfImmigrationStatus]: [DocumentType.ProofOfImmigrationStatus],
  [OfferTaskRoute.ProofOfManagerialExperience]: [DocumentType.ProofOfManagerialExperience],
  [OfferTaskRoute.ProofOfPayment]: [DocumentType.ProofOfPayment],
  [OfferTaskRoute.RequirementProofOfPayment]: [DocumentType.ProofOfPayment],
  [OfferTaskRoute.ResearchProposal]: [DocumentType.ResearchProposal],
  [OfferTaskRoute.Custom]: [DocumentType.CustomRequirement],
};
